import React, { useState } from "react";
import { Table } from "antd";
import Header from "../Header";
import Sidebar from "../Sidebar";
import { pdficon, pdficon3, refreshicon } from "../imagepath";
import { Link } from "react-router-dom";
import FeatherIcon from "feather-icons-react/build/FeatherIcon";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getMedicineStockReport } from "../../connections/getMedicineStockReport";
import jsPDF from "jspdf";
import "jspdf-autotable";

const isBatchExpired = (expiryDateStr) => {
  if (!expiryDateStr) return false;
  const expiry = new Date(expiryDateStr);
  expiry.setHours(23, 59, 59, 999);
  return expiry < new Date();
};

const batchColumns = [
  {
    title: "Batch No.",
    dataIndex: "batch_no",
    key: "batch_no",
    render: (text, record) => (
      <span style={isBatchExpired(record.expiry_date) ? { color: "#c0392b", fontWeight: 600 } : {}}>
        {text || "-"}
      </span>
    ),
  },
  {
    title: "Expiry Date",
    dataIndex: "expiry_date",
    key: "expiry_date",
    render: (text, record) => (
      <span style={isBatchExpired(record.expiry_date) ? { color: "#c0392b", fontWeight: 600 } : {}}>
        {text || "-"}
        {isBatchExpired(record.expiry_date) && (
          <span
            style={{
              marginLeft: 6,
              fontSize: 11,
              background: "#fdecea",
              color: "#c0392b",
              borderRadius: 4,
              padding: "1px 5px",
              fontWeight: 600,
            }}
          >
            Expired
          </span>
        )}
      </span>
    ),
  },
  {
    title: "Packing",
    dataIndex: "packing",
    key: "packing",
    render: (text) => text || "-",
  },
  {
    title: "Stock Left",
    dataIndex: "stock_left",
    key: "stock_left",
    align: "right",
    render: (text) => text ?? "-",
  },
  {
    title: "Purchase Cost",
    dataIndex: "purchase_cost",
    key: "purchase_cost",
    align: "right",
    render: (val) => (val != null ? Number(val).toFixed(2) : "-"),
  },
  {
    title: "GST %",
    dataIndex: "gst",
    key: "gst",
    align: "right",
    render: (text) => (text ? `${text}%` : "-"),
  },
  {
    title: "CGST %",
    dataIndex: "cgst",
    key: "cgst",
    align: "right",
    render: (text) => (text ? `${text}%` : "-"),
  },
  {
    title: "SGST %",
    dataIndex: "sgst",
    key: "sgst",
    align: "right",
    render: (text) => (text ? `${text}%` : "-"),
  },
];

// ---- same logic as stockReportExport.js ----
const HEADERS = [
  "Id", "Name", "Form type", "Dose", "Frequency", "Duration",
  "Is deleted", "Company", "Amount (MRP)", "Discount", "Purchase cost",
  "GST %", "CGST %", "SGST %", "Total stock left", "Stock value (at cost)",
  "Stock value (at MRP)", "Expiry date", "Flat price", "Hsn code",
  "Hsn name", "Batch numbers", "Created at", "Updated at",
];

const csvCell = (v) => {
  if (v === null || v === undefined) return "";
  let s = typeof v === "object" ? JSON.stringify(v) : String(v);
  if (/[",\n]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
};

const buildRows = (payload) => {
  const rows = (payload.medicines || []).map((m) => [
    m.id, m.name, m.form_type, m.dose, m.frequency, m.duration,
    m.is_deleted, m.company, m.amount, m.discount, m.purchase_cost,
    m.gst, m.cgst, m.sgst, m.total_stock_left,
    m.stock_value_at_cost, m.stock_value_at_mrp,
    m.expiry_date, m.flat_price, m.hsn_code, m.hsn_name,
    (m.batches || []).map((b) => b.batch_no).filter(Boolean).join(", "),
    m.created_at, m.updated_at,
  ]);

  const t = payload.totals || {};
  const totals = new Array(HEADERS.length).fill("");
  totals[1] = "TOTAL";
  totals[14] = t.total_stock_left || 0;
  totals[15] = Math.round((Number(t.stock_value_at_cost) || 0) * 100) / 100;
  totals[16] = Math.round((Number(t.stock_value_at_mrp) || 0) * 100) / 100;
  rows.push(totals);
  return rows;
};
// -----------------------------------------------

const MedicineStockReport = () => {
  const {
    user: { user },
  } = useSelector((store) => store);

  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["getMedicineStockReport"],
    queryFn: () => getMedicineStockReport(user?.token),
    enabled: !!user?.token,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const medicines = data?.payload?.medicines ?? [];

  const handleCsvExport = () => {
    if (!medicines.length) return;
    setExportingCsv(true);
    try {
      const rows = buildRows(data?.payload || {});
      const csv = [HEADERS, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
      const blob = new Blob([String.fromCharCode(0xFEFF) + csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      link.download = `medicine_stock_report_${today}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      toast.error("CSV export failed");
    } finally {
      setExportingCsv(false);
    }
  };

  const handlePdfExport = () => {
    if (!medicines.length) return;
    setExportingPdf(true);
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });

      doc.setFontSize(14);
      doc.setFont(undefined, "bold");
      doc.text("Medicine Stock Report", doc.internal.pageSize.getWidth() / 2, 14, { align: "center" });
      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      const today = new Date().toISOString().slice(0, 10);
      doc.text(`Generated on: ${today}`, doc.internal.pageSize.getWidth() / 2, 20, { align: "center" });

      const pdfHeaders = [
        "ID", "Name", "Form", "Company", "HSN", "Total Stock",
        "Purchase Cost", "Stock Val (Cost)", "Stock Val (MRP)", "Flat Price", "GST %", "Expiry", "Batch Nos",
      ];
      const bodyData = medicines.map((m) => [
        m.id ?? "",
        m.name ?? "",
        m.form_type ?? "",
        m.company ?? "",
        m.hsn_code || m.hsn_name || "",
        m.total_stock_left ?? 0,
        m.purchase_cost != null ? Number(m.purchase_cost).toFixed(2) : "",
        m.stock_value_at_cost != null ? Number(m.stock_value_at_cost).toFixed(2) : "",
        m.stock_value_at_mrp != null ? Number(m.stock_value_at_mrp).toFixed(2) : "",
        m.flat_price != null ? Number(m.flat_price).toFixed(2) : "",
        m.gst ? `${m.gst}%` : "",
        m.expiry_date ?? "",
        (m.batches || []).map((b) => b.batch_no).filter(Boolean).join(", "),
      ]);

      const t = data?.payload?.totals || {};
      bodyData.push([
        { content: "TOTAL", colSpan: 5, styles: { fontStyle: "bold", halign: "right", fillColor: [240, 240, 240] } },
        { content: String(t.total_stock_left || 0), styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
        { content: "", styles: { fillColor: [240, 240, 240] } },
        { content: t.stock_value_at_cost != null ? Number(t.stock_value_at_cost).toFixed(2) : "", styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
        { content: t.stock_value_at_mrp != null ? Number(t.stock_value_at_mrp).toFixed(2) : "", styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
        { content: "", styles: { fillColor: [240, 240, 240] } },
        { content: "", styles: { fillColor: [240, 240, 240] } },
        { content: "", styles: { fillColor: [240, 240, 240] } },
        { content: "", styles: { fillColor: [240, 240, 240] } },
      ]);

      doc.autoTable({
        startY: 26,
        head: [pdfHeaders],
        body: bodyData,
        theme: "grid",
        headStyles: { fillColor: [249, 249, 249], textColor: [0, 0, 0], fontStyle: "bold", fontSize: 8, lineWidth: 0.3, lineColor: [0, 0, 0] },
        bodyStyles: { fontSize: 7, textColor: [0, 0, 0], lineWidth: 0.3, lineColor: [0, 0, 0] },
        margin: { top: 26, right: 8, bottom: 14, left: 8 },
        didDrawPage: (pageData) => {
          doc.setFontSize(8);
          doc.text(
            `Page ${pageData.pageNumber} of ${pageData.pageCount}`,
            doc.internal.pageSize.getWidth() - 8, 10, { align: "right" }
          );
        },
      });

      doc.save(`medicine_stock_report_${today}.pdf`);
    } catch {
      toast.error("PDF export failed");
    } finally {
      setExportingPdf(false);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "Medicine Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text || "-"}</strong>,
    },
    {
      title: "Form",
      dataIndex: "form_type",
      key: "form_type",
      render: (text) => text || "-",
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render: (text) => text || "-",
    },
    {
      title: "HSN",
      key: "hsn",
      render: (_, record) =>
        record.hsn_code || record.hsn_name || "-",
    },
    {
      title: "Total Stock",
      dataIndex: "total_stock_left",
      key: "total_stock_left",
      align: "right",
      render: (val) => (
        <span style={{ fontWeight: 600, color: val > 0 ? "#27ae60" : "#c0392b" }}>
          {val ?? 0}
        </span>
      ),
    },
    {
      title: "Purchase Cost",
      dataIndex: "purchase_cost",
      key: "purchase_cost",
      align: "right",
      render: (val) => (val != null ? Number(val).toFixed(2) : "-"),
    },
    {
      title: "Stock Value (Cost)",
      dataIndex: "stock_value_at_cost",
      key: "stock_value_at_cost",
      align: "right",
      render: (val) => (val != null ? Number(val).toFixed(2) : "-"),
    },
    {
      title: "Stock Value (MRP)",
      dataIndex: "stock_value_at_mrp",
      key: "stock_value_at_mrp",
      align: "right",
      render: (val) => (val != null ? Number(val).toFixed(2) : "-"),
    },
    {
      title: "Flat Price",
      dataIndex: "flat_price",
      key: "flat_price",
      align: "right",
      render: (val) => (val != null ? Number(val).toFixed(2) : "-"),
    },
    {
      title: "GST %",
      dataIndex: "gst",
      key: "gst",
      align: "right",
      render: (text) => (text ? `${text}%` : "-"),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (text) => text || "-",
    },
    {
      title: "Batches",
      key: "batches_count",
      align: "center",
      render: (_, record) => (
        <span className="custom-badge status-green">
          {record.batches?.length ?? 0}
        </span>
      ),
    },
  ];

  const expandedRowRender = (record) => {
    const batches = record.batches ?? [];
    if (!batches.length) {
      return (
        <div style={{ padding: "8px 16px", color: "#888", fontSize: 13 }}>
          No batches available.
        </div>
      );
    }
    return (
      <Table
        columns={batchColumns}
        dataSource={batches}
        rowKey={(b) => b.id}
        pagination={false}
        size="small"
        rowClassName={(batch) =>
          isBatchExpired(batch.expiry_date) ? "expired-batch-row" : ""
        }
        style={{ margin: "4px 0" }}
      />
    );
  };

  return (
    <>
      <style>{`
        .expired-batch-row td {
          background-color: #fdecea !important;
        }
        .expired-batch-row:hover td {
          background-color: #fad4d0 !important;
        }
      `}</style>

      <Header />
      <Sidebar activeClassName="medicine-stock-report" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Export Report</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <FeatherIcon icon="chevron-right" />
                  </li>
                  <li className="breadcrumb-item active">Medicine Stock Report</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card card-table show-entire">
                <div className="card-body">
                  <div className="page-table-header mb-2">
                    <div className="row align-items-center">
                      <div className="col">
                        <div className="doctor-table-blk">
                          <h3>Medicine Stock Report</h3>
                          <div className="doctor-search-blk">
                            <div className="add-group">
                              <Link
                                onClick={refetch}
                                className="btn btn-primary add-pluss ms-2"
                              >
                                <img src={refreshicon} alt="#" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                      {!isLoading && medicines.length > 0 && (
                        <div className="col-auto text-end float-end ms-auto download-grp d-flex align-items-center gap-2">
                          {exportingPdf ? (
                            <span title="Generating PDF…" style={{ display: "inline-flex", alignItems: "center" }}>
                              <i className="fa fa-spinner fa-spin" style={{ fontSize: 20, color: "#666" }} />
                            </span>
                          ) : (
                            <img
                              className="me-2"
                              onClick={handlePdfExport}
                              src={pdficon}
                              alt="PDF"
                              style={{ cursor: "pointer" }}
                            />
                          )}
                          {exportingCsv ? (
                            <span title="Generating CSV…" style={{ display: "inline-flex", alignItems: "center" }}>
                              <i className="fa fa-spinner fa-spin" style={{ fontSize: 20, color: "#666" }} />
                            </span>
                          ) : (
                            <img
                              onClick={handleCsvExport}
                              className="me-2"
                              src={pdficon3}
                              alt="CSV"
                              style={{ cursor: "pointer" }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="table-responsive doctor-list">
                    <Table
                      loading={isLoading}
                      pagination={false}
                      columns={columns}
                      dataSource={medicines}
                      rowKey={(record) => record.id}
                      expandable={{
                        expandedRowRender,
                        rowExpandable: (record) =>
                          record.batches && record.batches.length > 0,
                      }}
                      scroll={{ x: "max-content" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MedicineStockReport;
